import { useState } from "react";

import classNames from "classnames";
import currency from "currency.js";
import { keyBy } from "lodash-es";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom-v5-compat";

import { DietaryEntities } from "Helpers/DataDefinitions/Dietaries";

import { Button } from "@onsite_app/MobileOrderingMenu/Atoms";
import {
  Price,
  PrimaryAction,
  TitleBar,
  useSubmitButton,
} from "@onsite_app/MobileOrderingMenu/Atoms";
import { Dietary } from "@onsite_app/MobileOrderingMenu/Atoms";
import { Session } from "@onsite_app/MobileOrderingMenu/Authentication";
import { useVenue } from "@onsite_app/MobileOrderingMenu/VenueContext";
import {
  useFetchLiveItemCountdown,
  useFetchMenu,
} from "@onsite_app/MobileOrderingMenu/queries";
import type {
  Menu,
  Item,
  ItemMod,
  ItemModGroup,
  Venue,
} from "@onsite_app/MobileOrderingMenu/schema";

import { Cart, CartItem, ItemMod as CartItemMod } from "../Cart/schema";
import { ItemCountdown } from "../Sections/Section";
import { useSession, useSessionDispatch } from "../Session/SessionContext";
import {
  getCanAddToCart,
  getIsWishlist,
  isRunningTab,
} from "../Session/sessionSelectors";
import {
  CartItemModAttributesRequest,
  CartItemRequest,
  createCartItem,
  deleteCartItem,
  updateCartItem,
} from "./cartItemRequests";

interface ItemModFormData {
  modGroupUuid: string;
  itemModUuid: string;
  cartItemUuid?: string;
}

type OptionalModFormData = false | string | string[];

interface ItemFormData {
  modGroups?: {
    mandatory: Record<string, ItemModFormData>;
    // React hook form has a bug where it doesn't handle arrays of arrays correctly
    // It supports 3 different case each which return a different type this is super annoying
    // 1 optional mod but non selected returns false
    // 1 optional mod selected returns string ( the mod uuid)
    // Multiple optional mods returns string[] always
    optional: Record<string, OptionalModFormData>;
  };

  // Because of how react-hook-form handles className="hidden || invisible"  inputs, we need to use a <input type="hidden" />
  // Which produces a string value, so for full type safety we need to use a string here
  multiplier: string | number;
}

const parseOptionalMods = (optionalMods?: OptionalModFormData) => {
  if (!optionalMods) return [];
  if (typeof optionalMods === "string") return [optionalMods];
  return optionalMods;
};
const getDisableOrdering = (session: Session, venue: Venue) => {
  return isRunningTab(session) && !venue.enabledFeatures.tablePayOrdering;
};
const isFavouriteMod = (mod: ItemMod) => mod.favourite;
const isMandatory = (mod: CartItemMod) => mod.modType == "mandatory";
const isOptional = (mod: CartItemMod) => mod.modType == "optional";
const isMandatoryModGroup = (mod: ItemModGroup) =>
  mod.modGroupType == "mandatory";
const isOptionalModGroup = (modGroup: ItemModGroup) =>
  !isMandatoryModGroup(modGroup);
const isEmptyModGroup = (group: ItemModGroup) => group.itemMods.length > 0;

const itemModToFormData = (itemMod: ItemMod): ItemModFormData => ({
  modGroupUuid: itemMod.uuid,
  itemModUuid: itemMod.uuid,
  cartItemUuid: undefined,
});

const cartItemModToFormData = (cartItemMod: CartItemMod): ItemModFormData => ({
  modGroupUuid: cartItemMod.itemModGroupId,
  itemModUuid: cartItemMod.itemModId,
  cartItemUuid: cartItemMod.id,
});
const generateDefaultFormData = (
  item: Item,
  cartItem?: CartItem,
): ItemFormData => {
  const defaultModGroups: Record<string, ItemModFormData> = item.itemModGroups
    .filter(isMandatoryModGroup)
    .filter(isEmptyModGroup)
    .reduce(
      (acc, modGroup): Record<string, ItemModFormData> => ({
        ...acc,
        [modGroup.uuid]: modGroup.itemMods
          .filter(isFavouriteMod)
          .map(itemModToFormData)[0],
      }),
      {} as Record<string, ItemModFormData>,
    );
  const formData: ItemFormData = {
    modGroups: {
      optional: cartItem
        ? cartItem.itemMods.filter(isOptional).reduce(
            (acc, cartItem) => ({
              [cartItem.itemModGroupId]: [
                ...(acc[cartItem.itemModGroupId] || []),
                cartItem.itemModId,
              ],
            }),
            {} as Record<string, string[]>,
          )
        : {},
      mandatory: (cartItem?.itemMods || []).filter(isMandatory).reduce(
        (acc, cartItemMod) => ({
          ...acc,
          [cartItemMod.itemModGroupId]: cartItemModToFormData(cartItemMod),
        }),
        defaultModGroups,
      ),
    },
    multiplier: cartItem?.multiplier || 1,
  };
  return formData;
};
const getItemModsByUuid = (item: Item) =>
  keyBy(
    item.itemModGroups.flatMap((modGroup) => modGroup.itemMods),
    "uuid",
  );
const generateCartItem =
  (cart: Cart, cartItem?: CartItem) =>
  (item: Item, result: Partial<ItemFormData>): CartItemRequest => {
    const itemMods = getItemModsByUuid(item);
    const selectedMandatoryMods: CartItemModAttributesRequest[] =
      Object.entries(result.modGroups?.mandatory || {}).map(
        ([modGroupUuid, itemModFormData]) => ({
          modType: "mandatory",
          itemModId: itemModFormData.itemModUuid,
          itemModGroupId: modGroupUuid,
          salesPriceId: itemMods[itemModFormData.itemModUuid].salesPriceUuid,
          id: itemModFormData.cartItemUuid,
        }),
      );

    const selectedOptionalMods: CartItemModAttributesRequest[] =
      item.itemModGroups.filter(isOptionalModGroup).flatMap((modGroup) =>
        modGroup.itemMods
          .map(
            (itemMod): CartItemModAttributesRequest => ({
              modType: "optional",
              itemModId: itemMod.uuid,
              itemModGroupId: modGroup.uuid,
              salesPriceId: itemMod.salesPriceUuid,
              id: cartItem?.itemMods.find(
                (cartItemMod) => cartItemMod.itemModId == itemMod.uuid,
              )?.id,
              delete: !parseOptionalMods(
                result.modGroups?.optional[modGroup.uuid],
              ).some((itemModFormData) => itemModFormData == itemMod.uuid),
            }),
          )
          .filter((itemMod) => !(itemMod.delete && !itemMod.id)),
      );

    const missingCartData: CartItemRequest = {
      cartId: cart.uuid,
      itemId: item.uuid,
      multiplier: +(result?.multiplier || 1),
      itemModsAttributes: [...selectedOptionalMods, ...selectedMandatoryMods],
      salesPriceId: item.salesPriceUuid,
    };

    return missingCartData;
  };

const previewPrice = (item: Item, formData: ItemFormData): number => {
  const itemMods = getItemModsByUuid(item);

  const mandatoryMods = formData.modGroups?.mandatory || {};
  const optionalMods = formData.modGroups?.optional || {};
  const mandatoryPrice = Object.values(mandatoryMods)
    .filter((i) => i)
    .map(
      (itemModFormData) =>
        itemMods[itemModFormData.itemModUuid]?.salesPrice || 0,
    );
  const optionalModPrice = Object.values(optionalMods).flatMap((itemModUuids) =>
    parseOptionalMods(itemModUuids).map(
      (itemModUuid) => itemMods[itemModUuid]?.salesPrice || 0,
    ),
  );
  return [item.salesPrice, ...mandatoryPrice, ...optionalModPrice]
    .reduce((a, b) => currency(a).add(b), currency(0))
    .multiply(+formData.multiplier).value;
};
const CartItemForm = ({
  cartItem,
  defaultFormData,
  item,
  itemCount,
  onCompletion,
  isEditing,
  onDeletion,
}: {
  menu: Menu;
  cartItem?: CartItem;
  defaultFormData: ItemFormData;
  item: Item;
  itemCount?: number;
  isEditing?: boolean;
  onCompletion: (result: CartItemRequest) => Promise<void>;
  onDeletion?: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { cart } = useSession();
  const session = useSession();
  const venue = useVenue();
  const nonEmptyModGroups = item.itemModGroups
    .filter(isEmptyModGroup)
    .map((modGroup) => ({
      ...modGroup,
      favouriteItemMod:
        modGroup.itemMods.find(isFavouriteMod) || modGroup.itemMods[0],
    }));

  const { register, watch, handleSubmit, ...formProps } = useForm<ItemFormData>(
    { defaultValues: defaultFormData },
  );
  const onSubmit = async (result: ItemFormData) => {
    const cartItemRequest = generateCartItem(cart, cartItem)(item, result);
    setRecentlyAdded(true);
    setTimeout(() => {
      setRecentlyAdded(false);
    }, 2000);
    await onCompletion(cartItemRequest);
  };

  const price = previewPrice(item, watch());
  const incrementMultiplier = (value: number) => () => {
    const newValue: number = Math.max(
      1,
      +formProps.getValues("multiplier") + value,
    );
    formProps.setValue("multiplier", newValue);
  };

  const [recentlyAdded, setRecentlyAdded] = useState(false);
  const [submissionState, trackSubmit] = useSubmitButton();
  const [deleteSubmissionState, trackDelete] = useSubmitButton();
  const showRightSide = nonEmptyModGroups.length > 0;
  const canAddToCart = getCanAddToCart(session, venue);
  const isWishList = getIsWishlist(session, venue);
  const editingActionContent = isWishList
    ? t("items.updateWishList")
    : t("items.updateOrderItem");
  const wishListActionContent = recentlyAdded ? (
    t("items.addedToWishlist")
  ) : (
    <>
      {t("items.addToWishlist")} (<Price showCurrency value={price} />)
    </>
  );
  const orderActionContent = recentlyAdded ? (
    t("items.addedToOrder")
  ) : (
    <>
      {t("items.addToOrder")}{" "}
      <span className="inline-block">
        <Price showCurrency value={price} />
      </span>
    </>
  );

  const primaryActionContent = isWishList
    ? wishListActionContent
    : orderActionContent;

  const isOrderingDisabled = getDisableOrdering(session, venue);
  const primaryActions = !isOrderingDisabled ? (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 gap-8">
        <div className="flex-1">
          <MultiplierIncrementer
            multiplier={+watch("multiplier")}
            maxMultiplier={itemCount}
            incrementMultiplier={incrementMultiplier(1)}
            decrementMultiplier={incrementMultiplier(-1)}
          />
        </div>
        {isEditing && onDeletion && (
          <div className="flex-1">
            <Button
              alert
              type="button"
              submissionState={deleteSubmissionState}
              onClick={trackDelete(onDeletion)}
            >
              {t("items.removeFromOrder")}
            </Button>
          </div>
        )}

        <div className="flex-1">
          <Button primary submissionState={submissionState}>
            <div className="whitespace-nowrap">
              {isEditing ? editingActionContent : primaryActionContent}
            </div>
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <div className="text-center">{t("items.orderingDisabled")}</div>
      <Button primary> {t("items.returnToOrder")} </Button>
    </div>
  );
  return (
    <div>
      <form onSubmit={handleSubmit(trackSubmit(onSubmit))}>
        <input type="hidden" {...register("multiplier")} />
        <div className="md:relative md:space-y-32 md:rounded-venue-brand md:border-[6px] md:border-venue-tertiary md:px-16 md:py-16">
          <TitleBar alwaysShowBackButton>
            <div className="flex items-center justify-center gap-12">
              <div className="text-3xl font-bold">{item.name}</div>
              <div className="my-auto">
                <ItemCountdown count={itemCount} />
              </div>
            </div>
          </TitleBar>
          <div className="space-y-48">
            <div className="space-y-32 md:flex md:justify-center md:gap-32 md:space-y-0">
              <div className="flex-1 space-y-24 md:w-1/2 md:flex-none">
                <div>
                  {
                    item.heroImageUrl ? (
                      <div className="aspect-h-2 aspect-w-3 -mx-16 md:mx-0">
                        <img
                          src={item.heroImageUrl}
                          className="object-cover shadow-venue-brand-style md:rounded-venue-brand"
                        />
                      </div>
                    ) : null
                    // This still needs to be sussed if using or not
                    // <div className="bg-venue-primary text-center text-3xl py-16 text-venue-secondary">
                    //   {item.name}
                    // </div>
                  }
                </div>
                <div className="space-y-16">
                  <div className="flex flex-wrap gap-12">
                    {item.dietaries
                      .map((dietary) => DietaryEntities[dietary])
                      .map((dietary) => (
                        <Dietary dietary={dietary} key={dietary.value} />
                      ))}
                  </div>
                  <div className="text-2xl font-bold md:hidden">
                    <Price value={price} />
                  </div>
                  <div className="text-base font-normal">
                    {item.marketingBlurb}
                  </div>
                  <div className="text-base font-normal">{item.detail}</div>
                </div>
              </div>
              {showRightSide && (
                <div className="space-y-32 md:flex-1">
                  {nonEmptyModGroups.map((modGroup) => (
                    <div key={modGroup.uuid} className="space-y-12">
                      <div>
                        <h2 className="text-2xl font-bold">{modGroup.name}</h2>
                        <div className="text-base font-semibold italic">
                          {t(`items.modGroupType.${modGroup.modGroupType}`)}
                        </div>
                      </div>
                      <div className="space-y-12">
                        {modGroup.itemMods.map((mod) => (
                          <label
                            key={mod.uuid}
                            className="flex cursor-pointer items-center gap-8 rounded-venue-brand bg-venue-tertiary p-8 text-base font-medium shadow-venue-brand-style"
                          >
                            <div className="flex-none">
                              {isMandatoryModGroup(modGroup) ? (
                                <>
                                  <input
                                    type="hidden"
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.itemModGroupId`,
                                    )}
                                  />
                                  <input
                                    type="hidden"
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.cartItemUuid`,
                                    )}
                                  />
                                  <input
                                    type={"radio"}
                                    id={`modGroups[mandatory][${modGroup.uuid}].itemModUuid`}
                                    {...register(
                                      `modGroups.mandatory.${modGroup.uuid}.itemModUuid`,
                                    )}
                                    value={mod.uuid}
                                    defaultChecked={
                                      modGroup.favouriteItemMod.uuid == mod.uuid
                                    }
                                  />
                                </>
                              ) : (
                                <input
                                  type={"checkbox"}
                                  id={`modGroups[optional][${modGroup.uuid}]`}
                                  {...register(
                                    `modGroups.optional.${modGroup.uuid}`,
                                  )}
                                  value={mod.uuid}
                                />
                              )}
                            </div>

                            <div className="flex-1">{mod.name}</div>
                            <div className="flex-none">
                              <Price value={mod.salesPrice} />
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bottom-0 hidden bg-venue-secondary px-16 py-8 md:sticky md:-mx-16 md:block">
              <div className="flex-1 items-center justify-between gap-12 md:flex">
                <div className="flex-1 text-3xl font-bold">
                  <Price value={price} />
                </div>

                {itemCount == undefined || (itemCount && itemCount > 0) ? (
                  canAddToCart ? (
                    <>
                      <div className="flex-1">
                        <MultiplierIncrementer
                          maxMultiplier={itemCount}
                          multiplier={+watch("multiplier")}
                          incrementMultiplier={incrementMultiplier(1)}
                          decrementMultiplier={incrementMultiplier(-1)}
                        />
                      </div>

                      {isEditing && onDeletion && (
                        <div className="flex-1">
                          <Button
                            alert
                            type="button"
                            submissionState={deleteSubmissionState}
                            onClick={trackDelete(onDeletion)}
                          >
                            {t("items.removeFromOrder")}
                          </Button>
                        </div>
                      )}

                      <div className="flex-1">
                        <Button primary submissionState={submissionState}>
                          {isEditing
                            ? editingActionContent
                            : primaryActionContent}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-xl font-bold">
                      {t("items.orderingDisabled")}
                    </div>
                  )
                ) : (
                  <div className="flex-1">
                    <Button primary disabled submissionState={submissionState}>
                      {t("items.soldOut")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PrimaryAction>{primaryActions}</PrimaryAction>
        </div>
      </form>
    </div>
  );
};

export const EditCartItem = () => {
  const menu = useFetchMenu();
  const { cartItemId } = useParams();
  const { t } = useTranslation();
  const { cart } = useSession();
  const navigate = useNavigate();
  const dispatch = useSessionDispatch();

  const cartItem = cart.items.find((i) => i.id == cartItemId);
  if (!cartItem) throw new Error(t("items.cartItemNotFound"));

  const itemUuid = cartItem.itemId;
  const item = menu.sections
    .flatMap((s) => s.items)
    .find((i) => i.uuid == itemUuid);
  if (!item) throw new Error(t("items.menuItemNotFound"));

  const onEditOrderItem = async (cartItem: CartItemRequest) => {
    if (!cartItemId) {
      throw new Error("No Cart Item Id");
    }
    const response = await updateCartItem({ id: cartItemId, ...cartItem });
    dispatch({ action: "updateCartItem", payload: response });
    navigate("/your-order/pending");
  };
  const removeOrderItem = async () => {
    await deleteCartItem(cart.uuid, cartItem.id);
    dispatch({
      action: "removeCartItem",
      payload: { cartItemId: cartItem.id },
    });
    navigate("/your-order/pending");
  };
  return (
    <CartItemForm
      menu={menu}
      cartItem={cartItem}
      defaultFormData={generateDefaultFormData(item, cartItem)}
      item={item}
      isEditing
      onCompletion={onEditOrderItem}
      onDeletion={removeOrderItem}
    />
  );
};

export const NewCartItem = () => {
  const menu = useFetchMenu();
  const { t } = useTranslation();
  const { itemUuid } = useParams();

  const navigate = useNavigate();
  const dispatch = useSessionDispatch();

  const item = menu.sections
    .flatMap((s) => s.items)
    .find((i) => i.uuid == itemUuid);
  if (!item) throw new Error(t("items.menuItemNotFound"));

  const onAddOrderItemMobile = async (request: CartItemRequest) => {
    const cartItem = await createCartItem(request);
    dispatch({ action: "addCartItem", payload: cartItem });
    navigate("/your-order/pending");
  };
  const onAddOrderItemDesktop = async (request: CartItemRequest) => {
    const cartItem = await createCartItem(request);
    dispatch({ action: "addCartItem", payload: cartItem });
  };
  const fetchLiveItemCountdowns = useFetchLiveItemCountdown(item.uuid);

  // This feels like over kill for what is essentially an if statement based on the browser size.
  // However because the mobile app takes you to the review order page when the desktop page does not
  // This kinda ends up working pretty well, even if we do end up rendering the form twice
  return (
    <>
      <div className="md:hidden">
        <CartItemForm
          menu={menu}
          itemCount={fetchLiveItemCountdowns.data?.remainingCount}
          defaultFormData={generateDefaultFormData(item)}
          item={item}
          onCompletion={onAddOrderItemMobile}
        />
      </div>
      <div className="hidden md:block">
        <CartItemForm
          menu={menu}
          defaultFormData={generateDefaultFormData(item)}
          item={item}
          itemCount={fetchLiveItemCountdowns.data?.remainingCount}
          onCompletion={onAddOrderItemDesktop}
        />
      </div>
    </>
  );
};
const MultiplierIncrementer = ({
  multiplier,
  incrementMultiplier,
  decrementMultiplier,
  maxMultiplier,
}: {
  multiplier: number;
  incrementMultiplier: () => void;
  decrementMultiplier: () => void;
  maxMultiplier?: number;
}) => {
  return (
    <div className="flex w-full items-center justify-around rounded-venue-brand border-2 border-venue-primary p-[10px] text-center text-xl font-semibold">
      <a
        className={classNames("flex-1 cursor-pointer px-4", {
          "opacity-30": multiplier <= 1,
        })}
        onClick={decrementMultiplier}
      >
        -
      </a>
      <div className="w-32 flex-1 text-center">{multiplier}</div>
      {maxMultiplier == undefined ||
      (maxMultiplier && multiplier < maxMultiplier) ? (
        <a className="flex-1 cursor-pointer px-4" onClick={incrementMultiplier}>
          +
        </a>
      ) : (
        <a className="flex-1"></a>
      )}
    </div>
  );
};
